import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faSpinner,
  faCalendarAlt,
  faCheck,
  faTimes,
  faTag,
  faBoxOpen,
  faCog,
  faFileContract,
  faGraduationCap,
  faExternalLinkAlt
} from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { formatCurrencyCentValue, formatDate } from '@/utils/helpers';
import Modal from '@/components/ui/Modal';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import {
  type Subscription, type SubscriptionEvent, type UserID, type Course, type CourseID
} from '@/models';
import { PAGINATION } from '@/config';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';

interface SubscriptionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: Subscription;
  userId: UserID;
}

function StatusBadge({ status }: { status: Subscription['status'] }) {
  const statusConfig: Record<Subscription['status'], { bg: string; text: string; label: string }> = {
    'ACTIVE': { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-800 dark:text-green-400', label: 'Aktiv' },
    'PENDING_PAYMENT': { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-400', label: 'Zahlung ausstehend' },
    'INCOMPLETE': { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-400', label: 'Unvollständig' },
    'PAST_DUE': { bg: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-800 dark:text-orange-400', label: 'Überfällig' },
    'CANCELED': { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-400', label: 'Gekündigt' },
    'ABORTED': { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-400', label: 'Checkout abgebrochen' },
    'FAILED': { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-400', label: 'Fehlgeschlagen' }
  };

  const config = statusConfig[status] ?? { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-400', label: status };

  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

function FeatureStatusBadge({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400">
      <FontAwesomeIcon icon={faCheck} className="w-2.5 h-2.5 mr-1" />
      Ja
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
      <FontAwesomeIcon icon={faTimes} className="w-2.5 h-2.5 mr-1" />
      Nein
    </span>
  );
}

export default function SubscriptionDetailModal({
  isOpen,
  onClose,
  subscription,
  userId
}: SubscriptionDetailModalProps) {
  const { getPeriodLabel } = useSubscriptionProducts();
  const [events, setEvents] = useState<SubscriptionEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalCount, setEventsTotalCount] = useState(0);
  const [courses, setCourses] = useState<Course[]>([]);
  const eventsPageSize = 10;

  useEffect(() => {
    if (isOpen) {
      fetchEvents();
      fetchCourses();
    }
  }, [isOpen, eventsPage]);

  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const cursorToken = eventsPage > 1 ? `$${eventsPage}` : undefined;
      const response = await APIRoutes.getSubscriptionEvents(userId, subscription.id, {
        limit: eventsPageSize,
        next: cursorToken
      });
      setEvents(response.events);
      setEventsTotalCount(response.total);
    } catch (error) {
      console.error('Error fetching subscription events:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await APIRoutes.listCourses({ limit: PAGINATION.FETCH_ALL_LIMIT });
      setCourses(response.courses);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const handleClose = () => {
    setEventsPage(1);
    setEvents([]);
    onClose();
  };

  const formatLimit = (limit: number | null): string => {
    if (limit === null) {
      return 'Kein Limit';
    }
    return formatCurrencyCentValue(limit, 'USD');
  };

  const getCourseName = (courseId: CourseID): string => {
    const course = courses.find(c => c.id === courseId);
    return course?.title ?? courseId;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Abonnement-Details"
      icon={<FontAwesomeIcon icon={faCreditCard} className="w-5 h-5 text-blue-600" />}
      maxWidth="4xl"
    >
      <div className="p-6 space-y-6">
        {/* General Info */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4 mr-2" />
            Allgemeine Informationen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
              <div className="mt-1">
                <StatusBadge status={subscription.status} />
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Zeitraum</span>
              <p className="text-gray-900 dark:text-white font-medium">
                {getPeriodLabel(subscription.items[0].type)}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Preis</span>
              <p className="text-gray-900 dark:text-white font-medium">
                {formatCurrencyCentValue(subscription.price, 'EUR')}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Erstellt</span>
              <p className="text-gray-900 dark:text-white">{formatDate(subscription.created)}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Aktualisiert</span>
              <p className="text-gray-900 dark:text-white">
                {subscription.updated !== null ? formatDate(subscription.updated) : '—'}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Abonnement-ID</span>
              <p className="text-gray-500 dark:text-gray-400 font-mono text-xs break-all">{subscription.id}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Empfehlungscode</span>
              <p className="text-gray-900 dark:text-white font-mono">
                {subscription.referrerCode !== null && subscription.referrerCode !== '' ? subscription.referrerCode : '—'}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Empfehlung durch</span>
              <p className="text-gray-900 dark:text-white">
                {subscription.referrer !== null && subscription.referrerUserID !== null ? (
                  <Link
                    to={`/admin/users/${subscription.referrerUserID}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {subscription.referrer.firstName} {subscription.referrer.lastName}
                  </Link>
                ) : '—'}
              </p>
            </div>
          </div>
        </section>

        {/* Stripe IDs */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Stripe-Informationen
          </h3>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Stripe Customer ID</span>
              <a
                href={`https://dashboard.stripe.com/customers/${subscription.stripeCustomerID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline font-mono text-xs break-all"
              >
                {subscription.stripeCustomerID}
                <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3 h-3 shrink-0" />
              </a>
            </div>
            {subscription.stripeSubscriptionID !== null && (
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Stripe Subscription ID</span>
                <a
                  href={`https://dashboard.stripe.com/subscriptions/${subscription.stripeSubscriptionID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline font-mono text-xs break-all"
                >
                  {subscription.stripeSubscriptionID}
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3 h-3 shrink-0" />
                </a>
              </div>
            )}
            {subscription.stripeCheckoutSessionID !== null && (
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Stripe Checkout Session ID</span>
                <a
                  href={`https://dashboard.stripe.com/payments/${subscription.stripeCheckoutSessionID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline font-mono text-xs break-all"
                >
                  {subscription.stripeCheckoutSessionID}
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3 h-3 shrink-0" />
                </a>
              </div>
            )}
          </div>
        </section>

        {/* Subscription Lifecycle */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2" />
            Laufzeit
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Aktuelle Periode Start</span>
              <p className="text-gray-900 dark:text-white">
                {subscription.currentPeriodStart !== null
                  ? new Date(subscription.currentPeriodStart).toLocaleDateString('de-DE')
                  : '—'}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Aktuelle Periode Ende</span>
              <p className="text-gray-900 dark:text-white">
                {subscription.currentPeriodEnd !== null
                  ? new Date(subscription.currentPeriodEnd).toLocaleDateString('de-DE')
                  : '—'}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Testphase endet</span>
              <p className="text-gray-900 dark:text-white">
                {subscription.trialEnd !== null
                  ? new Date(subscription.trialEnd).toLocaleDateString('de-DE')
                  : '—'}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Kündigung am Periodenende</span>
              <p className="text-gray-900 dark:text-white">
                {subscription.cancelAtPeriodEnd ? (
                  <span className="text-red-600 dark:text-red-400 font-medium">Ja</span>
                ) : (
                  <span>Nein</span>
                )}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Gekündigt am</span>
              <p className="text-gray-900 dark:text-white">
                {subscription.canceledAt !== null
                  ? new Date(subscription.canceledAt).toLocaleDateString('de-DE')
                  : '—'}
              </p>
            </div>
          </div>
        </section>

        {/* Subscription Items */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faBoxOpen} className="w-4 h-4 mr-2" />
            Abonnement-Elemente ({subscription.items.length})
          </h3>
          {subscription.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Typ</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Preis</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Kurs</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Stripe Product ID</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {subscription.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">{item.type}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatCurrencyCentValue(item.price, 'EUR')}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {item.courseID !== null ? getCourseName(item.courseID) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 font-mono">{item.stripeProductID}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm italic">Keine Elemente vorhanden</p>
          )}
        </section>

        {/* Applied Coupons */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faTag} className="w-4 h-4 mr-2" />
            Angewandte Gutscheine ({subscription.appliedCoupons.length})
          </h3>
          {subscription.appliedCoupons.length > 0 ? (
            <div className="space-y-3">
              {subscription.appliedCoupons.map((coupon, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {coupon.name ?? coupon.couponID}
                    </span>
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {coupon.percentOff !== null && coupon.percentOff > 0
                        ? `${coupon.percentOff}% Rabatt`
                        : coupon.amountOff !== null && coupon.amountOff > 0
                        ? `${formatCurrencyCentValue(coupon.amountOff, coupon.currency ?? 'EUR')} Rabatt`
                        : 'Rabatt'}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    <span>Dauer: {coupon.duration ?? '—'}</span>
                    {coupon.durationInMonths !== null && coupon.durationInMonths > 0 && (
                      <span className="ml-2">({coupon.durationInMonths} Monate)</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm italic">Keine Gutscheine angewandt</p>
          )}
        </section>

        {/* Feature Config from Subscription */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faCog} className="w-4 h-4 mr-2" />
            Feature-Konfiguration (Abonnement)
          </h3>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Core-Tradingideen</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.coreRecommendations} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Earnings-Tradingideen</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.earningRecommendations} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Swing-Trade</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.swingTradeRecommendations} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Langzeit</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.longTermRecommendations} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Boost</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.boostRecommendations} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Chatbot</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.chatbot} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Video-Chatbot</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.videoChatbot} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Journal</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.journal} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Video-Finder</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.videoSearch} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Alle Kurse</span>
                <FeatureStatusBadge enabled={subscription.featureConfig.allCourses} />
              </div>
            </div>
            {/* Limits */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Limits</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Core Strike:</span>{' '}
                  <span className="text-gray-900 dark:text-white">{formatLimit(subscription.featureConfig.coreRecommendationStrikeLimit)}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Earning Strike:</span>{' '}
                  <span className="text-gray-900 dark:text-white">{formatLimit(subscription.featureConfig.earningRecommendationStrikeLimit)}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Swing Preis:</span>{' '}
                  <span className="text-gray-900 dark:text-white">{formatLimit(subscription.featureConfig.swingTradeRecommendationPriceLimit)}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Langzeit Preis:</span>{' '}
                  <span className="text-gray-900 dark:text-white">{formatLimit(subscription.featureConfig.longTermRecommendationPriceLimit)}</span>
                </div>
              </div>
            </div>
            {/* Individual courses */}
            {Object.keys(subscription.featureConfig.courses).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  <FontAwesomeIcon icon={faGraduationCap} className="w-3.5 h-3.5 mr-1.5" />
                  Einzelne Kurse
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(subscription.featureConfig.courses).map(([courseId, enabled]) => (
                    <span
                      key={courseId}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                        enabled
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {getCourseName(courseId as CourseID)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Subscription Events */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faFileContract} className="w-4 h-4 mr-2" />
            Abonnement-Events ({eventsTotalCount})
          </h3>
          {eventsLoading ? (
            <div className="flex justify-center py-8">
              <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
          ) : events.length > 0 ? (
            <div className="space-y-4">
              {events.map((event) => (
                <div key={event.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">{event.stripeEventType}</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">{event.stripeEventID}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {event.processedSuccess ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400">
                          <FontAwesomeIcon icon={faCheck} className="w-2.5 h-2.5 mr-1" />
                          Erfolgreich
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400">
                          <FontAwesomeIcon icon={faTimes} className="w-2.5 h-2.5 mr-1" />
                          Fehler
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(event.created)}
                      </span>
                    </div>
                  </div>
                  {event.errorMessage !== null && (
                    <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-400">
                      {event.errorMessage}
                    </div>
                  )}
                  <div className="mt-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Event Data:</span>
                    <pre className="mt-1 p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300 overflow-x-auto max-h-48 overflow-y-auto">
                      {JSON.stringify(event.eventData, null, 2)}
                    </pre>
                  </div>
                </div>
              ))}
              <PageBasedPagination
                currentPage={eventsPage}
                totalCount={eventsTotalCount}
                pageSize={eventsPageSize}
                onPageChange={setEventsPage}
                loading={eventsLoading}
              />
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm italic">Keine Events vorhanden</p>
          )}
        </section>
      </div>
    </Modal>
  );
}
